import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ANNOTATION_KIND_CODER_SPAN } from './coder-span-types';
import { selectionToSourceRange } from './selection-to-source-range';
import type { ViewAnnotation } from './cmx-document-content-adapter';
import { CmxDocumentContentBody } from './cmx-document-content-body';
import { type CoderSpan, useCoderSpans } from './use-coder-spans';

/** Only a coder's own spans are removable from the document body. */
const ACTIVATABLE_KINDS = [ANNOTATION_KIND_CODER_SPAN];

export type AnnotatableCmxDocumentContentProps = {
  /** The body. Offsets are relative to this exact string. */
  text: string | null | undefined;
  /** The body's media type (`body.mediaType`). Absent means Markdown. */
  mediaType?: string;
  /** Producer annotations to show alongside the coder's own spans. */
  annotations?: ViewAnnotation[];
  /** Spans to start with, e.g. ones already loaded for this document. */
  initialSpans?: CoderSpan[];
  onSpanCreated?: (span: CoderSpan) => void;
  onSpanRemoved?: (id: string) => void;
  /** Suppresses span creation without hiding existing spans. */
  disabled?: boolean;
};

/**
 * A CMX Document Content body a coder can span-annotate. CDMX-20062.
 *
 * Creating a span is `mouseup` -> resolve the selection to a source range ->
 * store it. Removing one is a click on the span. Attaching a *code* to a span
 * is explicitly out of scope for CDMX-20062 and CDMX-20063.
 *
 * The listener is on `document` rather than the container because a drag that
 * ends outside the body still produces a valid selection inside it -- the same
 * reason the production AnnotatableDocument does it that way. The range is then
 * rejected unless both endpoints are inside this container.
 */
export const AnnotatableCmxDocumentContent: React.FC<
  AnnotatableCmxDocumentContentProps
> = ({
  text,
  mediaType,
  annotations = [],
  initialSpans = [],
  onSpanCreated,
  onSpanRemoved,
  disabled = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    spans,
    annotations: spanAnnotations,
    createSpan,
    removeSpan
  } = useCoderSpans(initialSpans);

  const handleMouseUp = useCallback(() => {
    if (disabled || !text) {
      return;
    }

    const range = selectionToSourceRange(
      containerRef.current,
      window.getSelection()
    );
    if (!range) {
      return;
    }

    const created = createSpan(range, text.substring(range.start, range.end));
    if (created) {
      onSpanCreated?.(created);
      // Clear the browser selection so the new highlight is what the coder
      // sees, rather than a blue overlay on top of it.
      window.getSelection()?.removeAllRanges();
    }
  }, [disabled, text, createSpan, onSpanCreated]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const handleActivate = useCallback(
    (annotationIds: string[]) => {
      // A mark carries every id covering it, so a coder's span overlapping a
      // producer's section arrives as both. Only remove an id this store owns
      // -- a section is not the coder's to delete.
      const owned = annotationIds.find((id) =>
        spans.some((span) => span.id === id)
      );
      if (owned) {
        removeSpan(owned);
        onSpanRemoved?.(owned);
      }
    },
    [spans, removeSpan, onSpanRemoved]
  );

  const combined = useMemo(
    () => [...annotations, ...spanAnnotations],
    [annotations, spanAnnotations]
  );

  return (
    <div ref={containerRef} className="cmx-document-content-annotatable">
      <CmxDocumentContentBody
        text={text}
        mediaType={mediaType}
        annotations={combined}
        anchors
        onAnnotationActivate={disabled ? undefined : handleActivate}
        activatableKinds={ACTIVATABLE_KINDS}
        activateLabel="Remove this span"
      />
    </div>
  );
};
